
/**
 * warehouse-area.js
 *
 * Data + state layer for the regional area selector.
 * No UI. No gating logic. Just: read areas, read/write selection, validate,
 * resolve to a warehouse.
 *
 * ── Fetch strategy ──────────────────────────────────────────────────────
 * Area data is read from a Liquid-rendered <script type="application/json">
 * tag (see snippets/warehouse-area-data.liquid), NOT a live Storefront API
 * call, and NOT a build-time static snapshot.
 *
 *   - It's server-rendered by Liquid on every page request, so it always
 *     reflects current metaobject contents (an admin toggling `active` off
 *     takes effect on the next page load, no rebuild/redeploy needed).
 *   - It avoids an extra client-side network round trip (Storefront API)
 *     just to get a handful of small, mostly-static rows.
 *   - It works even if a page never triggers the selector UI, since the
 *     data is just sitting in the DOM already.
 *
 *   DECISION POINT FOR THE TEAM: if the area list needs to update *within*
 *   a single page view without a reload (e.g. an admin edits it and an
 *   already-open tab should pick it up), this Liquid approach won't do
 *   that — a live Storefront API call would be needed instead. Flagging
 *   this rather than assuming; the module boundary below (getAvailableAreas)
 *   is the only place that would need to change if the team picks that path.
 *
 * ── "No area selected" contract ─────────────────────────────────────────
 * There is exactly one "no selection" state, represented by the exported
 * constant NO_AREA_SELECTED (currently `null`).
 *
 * getResolvedArea() returns NO_AREA_SELECTED when:
 *   - the customer has never selected an area, OR
 *   - the stored area_key doesn't match any currently active area
 *     (removed / renamed / deactivated since they last picked it)
 *
 * Both cases are indistinguishable on purpose — later steps (selector UI,
 * PDP, cart) should treat them identically: don't assume a warehouse, don't
 * gate Add to Cart / Buy Now, and prompt the customer to select an area.
 * The specific UI treatment (banner vs. forced modal, etc.) is out of scope
 * here; this module just guarantees the state is unambiguous.
 *
 * resolveWarehouse(areaKey) returns the same NO_AREA_SELECTED sentinel
 * (never throws, never guesses) if areaKey doesn't match a currently
 * active area.
 */

(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.WarehouseArea = factory();
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var STORAGE_KEY = "warehouse_area_selection";
  var DATA_ELEMENT_ID = "warehouse-area-data";

  // The single, well-defined "no area selected" state. Exported so callers
  // never need to guess what a falsy return means.
  var NO_AREA_SELECTED = null;

  var _areasCache = null;

  /**
   * Reads and parses the Liquid-rendered JSON payload from the DOM.
   * Cached per page load (the data doesn't change without a fresh page
   * render under the current fetch strategy — see file header).
   *
   * @returns {Array<{area_name: string, area_key: string, warehouse: string}>}
   */
  function getAvailableAreas() {
    if (_areasCache !== null) {
      return _areasCache;
    }

    var el = document.getElementById(DATA_ELEMENT_ID);
    if (!el) {
      console.warn(
        "[warehouse-area] Could not find #" +
          DATA_ELEMENT_ID +
          " on the page. Is snippets/warehouse-area-data.liquid included? Returning empty area list."
      );
      _areasCache = [];
      return _areasCache;
    }

    var parsed;
    try {
      parsed = JSON.parse(el.textContent || "[]");
    } catch (err) {
      console.error("[warehouse-area] Failed to parse area data JSON.", err);
      _areasCache = [];
      return _areasCache;
    }

    if (!Array.isArray(parsed)) {
      console.error("[warehouse-area] Area data JSON was not an array.");
      _areasCache = [];
      return _areasCache;
    }

    // Defensive shape-check so a malformed metaobject entry can't silently
    // produce a broken area downstream.
    _areasCache = parsed.filter(function (entry) {
      return (
        entry &&
        typeof entry.area_name === "string" &&
        typeof entry.area_key === "string" &&
        typeof entry.warehouse === "string"
      );
    });

    return _areasCache;
  }

  /**
   * Clears the in-memory areas cache. Mainly useful for tests, or if the
   * team later adds a live-refresh path (see decision point in file header)
   * and needs to force a re-read.
   */
  function _clearAreasCache() {
    _areasCache = null;
  }

  /**
   * Reads the raw stored area_key from localStorage. This is the RAW value
   * only — it is NOT validated against the current area list. Use
   * getResolvedArea() when you need a trustworthy value.
   *
   * @returns {string|null}
   */
  function getSelectedArea() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      // localStorage can throw in private-browsing modes on some browsers,
      // or if disabled by the user. Treat as "nothing stored" rather than
      // letting it break the page.
      console.warn("[warehouse-area] localStorage read failed.", err);
      return null;
    }
  }

  /**
   * Writes the given area_key to localStorage. No expiry/TTL — persists
   * until explicitly overwritten (e.g. the customer picks a different area).
   *
   * @param {string} areaKey
   */
  function setSelectedArea(areaKey) {
    if (typeof areaKey !== "string" || !areaKey) {
      console.warn(
        "[warehouse-area] setSelectedArea called with a non-string/empty value:",
        areaKey
      );
      return;
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, areaKey);
    } catch (err) {
      console.warn("[warehouse-area] localStorage write failed.", err);
    }
  }

  /**
   * The single source of truth for "what area is currently selected, if any,
   * and is that selection still valid?"
   *
   * - No stored value yet -> NO_AREA_SELECTED
   * - Stored value doesn't match any currently active area -> NO_AREA_SELECTED
   *   (does NOT throw, does NOT fall back to a guessed default)
   * - Stored value matches an active area -> the full area object
   *
   * @returns {{area_name: string, area_key: string, warehouse: string} | null}
   */
  function getResolvedArea() {
    var storedKey = getSelectedArea();
    if (!storedKey) {
      return NO_AREA_SELECTED;
    }

    var areas = getAvailableAreas();
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].area_key === storedKey) {
        return areas[i];
      }
    }

    // Stored key no longer matches anything active: treat exactly like
    // "no selection" per the documented contract. Do not error, do not guess.
    return NO_AREA_SELECTED;
  }

  /**
   * Given an area_key, returns its warehouse, or NO_AREA_SELECTED if the
   * key doesn't resolve to a currently active area. Never throws.
   *
   * @param {string} areaKey
   * @returns {string|null}
   */
  function resolveWarehouse(areaKey) {
    if (typeof areaKey !== "string" || !areaKey) {
      return NO_AREA_SELECTED;
    }

    var areas = getAvailableAreas();
    for (var i = 0; i < areas.length; i++) {
      if (areas[i].area_key === areaKey) {
        return areas[i].warehouse;
      }
    }

    return NO_AREA_SELECTED;
  }

  return {
    NO_AREA_SELECTED: NO_AREA_SELECTED,
    getAvailableAreas: getAvailableAreas,
    getSelectedArea: getSelectedArea,
    setSelectedArea: setSelectedArea,
    getResolvedArea: getResolvedArea,
    resolveWarehouse: resolveWarehouse,
    _clearAreasCache: _clearAreasCache // exposed for tests only
  };
});
